import { getLogLevel, setLogLevel, type LogLevel } from "../../core/logger";
import type { ConnectionStatus, AppLikeContext } from "../../core/connection";
import type { EventLog, EventLogStats } from "../../core/eventLog";
import { toJsonExport, toNdjson } from "../../core/export";
import { JsonTree } from "../JsonTree";

const SDK_VERSION = "1.0.14";
const LOG_LEVELS: LogLevel[] = [
  "trace",
  "debug",
  "info",
  "warn",
  "error",
  "silent",
];

type Props = {
  status: ConnectionStatus;
  mode: "live" | "mock" | undefined;
  appInstanceId: string | undefined;
  context: AppLikeContext | undefined;
  stats: EventLogStats;
  eventLog: EventLog;
};

function downloadText(filename: string, text: string, mime: string): void {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function SessionPanel({
  status,
  mode,
  appInstanceId,
  context,
  stats,
  eventLog,
}: Props): React.JSX.Element {
  const exportJson = (): void => {
    const text = toJsonExport(eventLog.entries(), {
      capturedAt: new Date().toISOString(),
      mode: mode ?? "live",
      ...(appInstanceId && { appInstanceId }),
      sdkVersion: SDK_VERSION,
      stats: eventLog.stats(),
    });
    downloadText("cawd-session.json", text, "application/json");
  };

  const exportNdjson = (): void => {
    downloadText(
      "cawd-session.ndjson",
      toNdjson(eventLog.entries()),
      "application/x-ndjson",
    );
  };

  return (
    <div className="session-panel">
      <dl className="session-facts">
        <dt>Status</dt>
        <dd>{status}</dd>
        <dt>Mode</dt>
        <dd>{mode ?? "n/a"}</dd>
        <dt>App instance id</dt>
        <dd>{appInstanceId ?? "n/a"}</dd>
        <dt>SDK version</dt>
        <dd>{SDK_VERSION}</dd>
        <dt>Events captured</dt>
        <dd>{stats.count}</dd>
        <dt>Capacity</dt>
        <dd>{stats.capacity}</dd>
        <dt>Evicted</dt>
        <dd>{stats.evicted}</dd>
      </dl>

      <h3>App context</h3>
      {context ? (
        <JsonTree value={context} />
      ) : (
        <p className="empty-state">No context yet.</p>
      )}

      <h3>Log level</h3>
      <select
        aria-label="Log level"
        defaultValue={getLogLevel()}
        onChange={(e) => setLogLevel(e.target.value as LogLevel)}
      >
        {LOG_LEVELS.map((l) => (
          <option key={l} value={l}>
            {l}
          </option>
        ))}
      </select>

      <h3>Session</h3>
      <div className="session-actions">
        <button type="button" onClick={exportJson}>
          Export JSON
        </button>
        <button type="button" onClick={exportNdjson}>
          Export NDJSON
        </button>
        <button type="button" onClick={() => eventLog.clear()}>
          Clear
        </button>
      </div>
    </div>
  );
}
